import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import RoleNavigation from '../../components/ui/RoleNavigation';
import AnalyticsFilterBar from './components/AnalyticsFilterBar';
import RevenueTrendsChart from './components/RevenueTrendsChart';
import TopCampaignsRanking from './components/TopCampaignsRanking';
import EngagementBenchmarks from './components/EngagementBenchmarks';
import EarningForecasts from './components/EarningForecasts';
import EarningsBreakdown from './components/EarningsBreakdown';
import StripePayoutHistory from './components/StripePayoutHistory';
import Icon from '../../components/AppIcon';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDateRangeStart = (range) => {
  const now = new Date();
  switch (range) {
    case '7d': return new Date(now.setDate(now.getDate() - 7))?.toISOString();
    case '30d': return new Date(now.setDate(now.getDate() - 30))?.toISOString();
    case '90d': return new Date(now.setDate(now.getDate() - 90))?.toISOString();
    case '6m': return new Date(now.setMonth(now.getMonth() - 6))?.toISOString();
    case '1y': return new Date(now.setFullYear(now.getFullYear() - 1))?.toISOString();
    default: return new Date(now.setDate(now.getDate() - 30))?.toISOString();
  }
};

const groupByMonth = (ledgerRows) => {
  const map = {};
  ledgerRows?.forEach(row => {
    const d = new Date(row?.created_at);
    const key = d?.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (!map?.[key]) map[key] = { month: key, earnings: 0, credits: 0 };
    if (row?.ledger_type === 'credit') {
      map[key].credits += parseFloat(row?.amount || 0);
      map[key].earnings += parseFloat(row?.amount || 0);
    }
  });
  return Object.values(map)?.sort((a, b) => new Date('1 ' + a?.month) - new Date('1 ' + b?.month));
};

// ─── Summary Stats Card ───────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, sub, color = 'text-primary', iconBg = 'bg-primary/10' }) => (
  <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon name={icon} size={20} className={color} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold data-text ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
    </div>
  </div>
);

// ─── Main Screen ──────────────────────────────────────────────────────────────

const InfluencerAnalyticsScreen = () => {
  const { user, userProfile } = useAuth();

  // Filters
  const [dateRange, setDateRange] = useState('30d');
  const [campaignType, setCampaignType] = useState('all');

  // Data states
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [scoreData, setScoreData] = useState({ score: 0, tier: 'bronze', totalEarned: 0, walletBalance: 0 });
  const [platformAverages, setPlatformAverages] = useState({
    submissionCount: 5, approvalRate: 70, totalEarned: 50, score: 250,
    campaignCount: 3, avgEarningPerCampaign: 12,
  });
  const [activeCampaignCount, setActiveCampaignCount] = useState(0);
  const [monthlyGrowth, setMonthlyGrowth] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [avgMonthlyEarning, setAvgMonthlyEarning] = useState(0);

  // Loading states
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [loadingPayouts, setLoadingPayouts] = useState(true);
  const [loadingScore, setLoadingScore] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const isLoading = loadingRevenue || loadingCampaigns || loadingTransactions;

  // ── Load Revenue Trends from wallet_ledger ──────────────────────────────────
  const loadRevenueTrends = useCallback(async () => {
    if (!user?.id) return;
    setLoadingRevenue(true);
    try {
      const since = getDateRangeStart(dateRange);
      const { data, error } = await supabase
        ?.from('wallet_ledger')
        ?.select('amount, ledger_type, created_at, balance_after')
        ?.eq('user_id', user?.id)
        ?.gte('created_at', since)
        ?.order('created_at', { ascending: true });

      if (!error && data) {
        const grouped = groupByMonth(data);
        setRevenueTrends(grouped);

        const total = data
          ?.filter(r => r?.ledger_type === 'credit')
          ?.reduce((s, r) => s + parseFloat(r?.amount || 0), 0);
        setTotalEarned(total);

        const avg = grouped?.length > 0 ? total / grouped?.length : 0;
        setAvgMonthlyEarning(avg);

        // Calculate month-over-month growth
        if (grouped?.length >= 2) {
          const last = grouped?.[grouped?.length - 1]?.earnings || 0;
          const prev = grouped?.[grouped?.length - 2]?.earnings || 0;
          const growth = prev > 0 ? ((last - prev) / prev) * 100 : 0;
          setMonthlyGrowth(growth);
        }
      }
    } catch (err) {
      console.error('[InfluencerAnalytics] loadRevenueTrends error:', err);
    } finally {
      setLoadingRevenue(false);
    }
  }, [user?.id, dateRange]);

  // ── Load Top Campaigns ──────────────────────────────────────────────────────
  const loadTopCampaigns = useCallback(async () => {
    if (!user?.id) return;
    setLoadingCampaigns(true);
    try {
      const since = getDateRangeStart(dateRange);
      let query = supabase
        ?.from('transactions')
        ?.select('id, amount, status, campaign_id, campaigns(id, title)')
        ?.eq('influencer_id', user?.id)
        ?.in('status', ['approved', 'paid', 'processing'])
        ?.gte('created_at', since);

      if (campaignType !== 'all') {
        query = query?.eq('transaction_type', campaignType);
      }

      const { data, error } = await query;

      if (!error && data) {
        // Group by campaign
        const campaignMap = {};
        data?.forEach(t => {
          const cid = t?.campaign_id || 'unknown';
          const title = t?.campaigns?.title || 'Campaign';
          if (!campaignMap?.[cid]) campaignMap[cid] = { id: cid, title, earned: 0, participations: 0 };
          campaignMap[cid].earned += parseFloat(t?.amount || 0);
          campaignMap[cid].participations += 1;
        });
        const sorted = Object.values(campaignMap)?.sort((a, b) => b?.earned - a?.earned);
        setTopCampaigns(sorted);
      }
    } catch (err) {
      console.error('[InfluencerAnalytics] loadTopCampaigns error:', err);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [user?.id, dateRange, campaignType]);

  // ── Load Transactions ───────────────────────────────────────────────────────
  const loadTransactions = useCallback(async () => {
    if (!user?.id) return;
    setLoadingTransactions(true);
    try {
      const since = getDateRangeStart(dateRange);
      let query = supabase
        ?.from('transactions')
        ?.select('id, amount, status, created_at, campaign_id, campaigns(title)')
        ?.eq('influencer_id', user?.id)
        ?.gte('created_at', since)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (campaignType !== 'all') {
        query = query?.eq('transaction_type', campaignType);
      }

      const { data, error } = await query;

      if (!error && data) {
        const mapped = data?.map(t => ({
          id: t?.id,
          amount: parseFloat(t?.amount || 0),
          status: t?.status || 'pending',
          campaignName: t?.campaigns?.title || 'Campaign',
          date: new Date(t?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        }));
        setTransactions(mapped);
      }
    } catch (err) {
      console.error('[InfluencerAnalytics] loadTransactions error:', err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [user?.id, dateRange, campaignType]);

  // ── Load Score & Tier ───────────────────────────────────────────────────────
  const loadScoreData = useCallback(async () => {
    if (!user?.id) return;
    setLoadingScore(true);
    try {
      const { data, error } = await supabase
        ?.from('scores')
        ?.select('total_score, tier, total_earned, wallet_balance, transaction_count, approved_count')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (!error && data) {
        setScoreData({
          score: data?.total_score || 0,
          tier: data?.tier || 'bronze',
          totalEarned: parseFloat(data?.total_earned || 0),
          walletBalance: parseFloat(data?.wallet_balance || 0),
          transactionCount: data?.transaction_count || 0,
          approvedCount: data?.approved_count || 0,
        });
      }
    } catch (err) {
      console.error('[InfluencerAnalytics] loadScoreData error:', err);
    } finally {
      setLoadingScore(false);
    }
  }, [user?.id]);

  // ── Load Payouts ────────────────────────────────────────────────────────────
  const loadPayouts = useCallback(async () => {
    if (!user?.id) return;
    setLoadingPayouts(true);
    try {
      const { data, error } = await supabase
        ?.from('stripe_payouts')
        ?.select('id, amount, currency, status, description, failure_message, arrival_date, stripe_payout_id, created_at')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (!error && data) setPayouts(data);
    } catch (err) {
      console.error('[InfluencerAnalytics] loadPayouts error:', err);
    } finally {
      setLoadingPayouts(false);
    }
  }, [user?.id]);

  // ── Load Active Campaign Count ──────────────────────────────────────────────
  const loadActiveCampaigns = useCallback(async () => {
    try {
      const { count } = await supabase
        ?.from('campaigns')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'active');
      setActiveCampaignCount(count || 0);
    } catch (err) {
      console.error('[InfluencerAnalytics] loadActiveCampaigns error:', err);
    }
  }, []);

  // ── Load Platform Averages (aggregate stats) ────────────────────────────────
  const loadPlatformAverages = useCallback(async () => {
    try {
      const { data, error } = await supabase
        ?.from('scores')
        ?.select('total_score, total_earned, transaction_count, approved_count')
        ?.limit(100);

      if (!error && data?.length > 0) {
        const avgScore = data?.reduce((s, r) => s + (r?.total_score || 0), 0) / data?.length;
        const avgEarned = data?.reduce((s, r) => s + parseFloat(r?.total_earned || 0), 0) / data?.length;
        const avgTxCount = data?.reduce((s, r) => s + (r?.transaction_count || 0), 0) / data?.length;
        const avgApproved = data?.reduce((s, r) => s + (r?.approved_count || 0), 0) / data?.length;
        const avgApprovalRate = avgTxCount > 0 ? (avgApproved / avgTxCount) * 100 : 70;

        setPlatformAverages({
          score: Math.round(avgScore),
          totalEarned: parseFloat(avgEarned?.toFixed(2)),
          submissionCount: Math.round(avgTxCount),
          approvalRate: Math.round(avgApprovalRate),
          campaignCount: 3,
          avgEarningPerCampaign: avgTxCount > 0 ? parseFloat((avgEarned / avgTxCount)?.toFixed(2)) : 12,
        });
      }
    } catch (err) {
      console.error('[InfluencerAnalytics] loadPlatformAverages error:', err);
    }
  }, []);

  // ── Sync Stripe ─────────────────────────────────────────────────────────────
  const handleSyncStripe = useCallback(async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('stripe-balance', { body: {} });
      if (!error && data?.payouts?.length > 0) {
        for (const payout of data?.payouts) {
          await supabase?.from('stripe_payouts')?.upsert({
            user_id: user?.id,
            stripe_payout_id: payout?.id || '',
            amount: payout?.amount || 0,
            currency: 'usd',
            status: payout?.status || 'pending',
            description: payout?.description || '',
            failure_message: payout?.failureMessage || '',
            arrival_date: payout?.arrivalDate || null,
          }, { onConflict: 'stripe_payout_id', ignoreDuplicates: false });
        }
        await loadPayouts();
      }
    } catch (err) {
      console.error('[InfluencerAnalytics] handleSyncStripe error:', err);
    } finally {
      setSyncing(false);
    }
  }, [user?.id, loadPayouts]);

  // ── Initial Load ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      loadRevenueTrends(),
      loadTopCampaigns(),
      loadTransactions(),
      loadScoreData(),
      loadPayouts(),
      loadActiveCampaigns(),
      loadPlatformAverages(),
    ]);
    window.scrollTo(0, 0);
  }, [user?.id]);

  // ── Re-fetch when filters change ────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    loadRevenueTrends();
    loadTopCampaigns();
    loadTransactions();
  }, [dateRange, campaignType]);

  // ── Real-time subscriptions ─────────────────────────────────────────────────
  useRealtimeSubscription({
    channelName: `analytics-wallet-${user?.id}`,
    table: 'wallet_ledger',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => loadRevenueTrends(),
  });

  useRealtimeSubscription({
    channelName: `analytics-transactions-${user?.id}`,
    table: 'transactions',
    filter: user?.id ? `influencer_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => { loadTopCampaigns(); loadTransactions(); },
  });

  useRealtimeSubscription({
    channelName: `analytics-scores-${user?.id}`,
    table: 'scores',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onUpdate: () => loadScoreData(),
  });

  useRealtimeSubscription({
    channelName: `analytics-payouts-${user?.id}`,
    table: 'stripe_payouts',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => loadPayouts(),
  });

  // ── Derived metrics ─────────────────────────────────────────────────────────
  const approvalRate = scoreData?.transactionCount > 0
    ? Math.round((scoreData?.approvedCount / scoreData?.transactionCount) * 100)
    : 0;

  const userMetrics = {
    score: scoreData?.score,
    totalEarned: scoreData?.totalEarned,
    submissionCount: scoreData?.transactionCount || 0,
    approvalRate,
    campaignCount: topCampaigns?.length || 0,
    avgEarningPerCampaign: topCampaigns?.length > 0
      ? parseFloat((totalEarned / topCampaigns?.length)?.toFixed(2))
      : 0,
  };

  const role = userProfile?.role || 'influencer';
  const userName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'User';

  return (
    <>
      <Helmet>
        <title>Influencer Analytics — SocialCents</title>
        <meta name="description" content="Track your revenue trends, campaign performance, and earning forecasts on SocialCents." />
      </Helmet>

      <RoleNavigation role={role} userName={userName} />

      <main className="min-h-screen bg-background pt-[60px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #14B8A6, #F59E0B)' }}>
                  <Icon name="BarChart3" size={16} color="#fff" />
                </div>
                <h1 className="text-2xl font-bold text-foreground font-heading">Influencer Analytics</h1>
              </div>
              <p className="text-sm text-muted-foreground">Performance insights & earning optimization</p>
            </div>

            {/* Live indicator */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Live data
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <AnalyticsFilterBar
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              campaignType={campaignType}
              onCampaignTypeChange={setCampaignType}
              onRefresh={() => {
                loadRevenueTrends();
                loadTopCampaigns();
                loadTransactions();
                loadScoreData();
                loadPayouts();
              }}
              loading={isLoading}
            />
          </div>

          {/* Summary Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard
              icon="Wallet"
              label="Wallet Balance"
              value={`$${scoreData?.walletBalance?.toFixed(2)}`}
              sub="Available to withdraw"
              color="text-primary"
              iconBg="bg-primary/10"
            />
            <StatCard
              icon="TrendingUp"
              label="Total Earned"
              value={`$${scoreData?.totalEarned?.toFixed(2)}`}
              sub={`${scoreData?.transactionCount || 0} transactions`}
              color="text-emerald-400"
              iconBg="bg-emerald-500/10"
            />
            <StatCard
              icon="Star"
              label="SocialCents Score"
              value={scoreData?.score}
              sub={`${scoreData?.tier?.charAt(0)?.toUpperCase()}${scoreData?.tier?.slice(1)} tier`}
              color="text-amber-400"
              iconBg="bg-amber-500/10"
            />
            <StatCard
              icon="CheckCircle"
              label="Approval Rate"
              value={`${approvalRate}%`}
              sub={`${scoreData?.approvedCount || 0} approved`}
              color="text-blue-400"
              iconBg="bg-blue-500/10"
            />
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

            {/* Revenue Trends — spans 2 cols */}
            <div className="xl:col-span-2">
              <RevenueTrendsChart
                data={revenueTrends}
                loading={loadingRevenue}
                totalEarned={totalEarned}
                monthlyGrowth={monthlyGrowth}
              />
            </div>

            {/* Top Campaigns */}
            <div className="xl:col-span-1">
              <TopCampaignsRanking
                campaigns={topCampaigns}
                loading={loadingCampaigns}
              />
            </div>

            {/* Engagement Benchmarks */}
            <div className="xl:col-span-1">
              <EngagementBenchmarks
                userMetrics={userMetrics}
                platformAverages={platformAverages}
                loading={loadingScore}
              />
            </div>

            {/* Earning Forecasts */}
            <div className="xl:col-span-2">
              <EarningForecasts
                tier={scoreData?.tier}
                avgMonthlyEarning={avgMonthlyEarning}
                activeCampaignCount={activeCampaignCount}
                loading={loadingScore}
              />
            </div>

            {/* Earnings Breakdown — full width */}
            <div className="xl:col-span-3">
              <EarningsBreakdown
                transactions={transactions}
                loading={loadingTransactions}
              />
            </div>

            {/* Stripe Payout History — full width */}
            <div className="xl:col-span-3">
              <StripePayoutHistory
                payouts={payouts}
                loading={loadingPayouts}
                onSyncStripe={handleSyncStripe}
                syncing={syncing}
              />
            </div>

          </div>
        </div>
      </main>
    </>
  );
};

export default InfluencerAnalyticsScreen;
